import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";
import {
  Button,
  Card,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { t } from "i18next";
import { Link } from "react-router-dom";
import { appRoutes } from "../../../../App";
import { CreateOrUpdateProjectModal } from "../CreateOrUpdateProject/CreateOrUpdateProjectModal";
import { Project } from "../../models/Project";
import { useStores } from "../../../shared/stores/RootStore";
import { AddIcon, CloseIcon, HamburgerIcon } from "../../../shared/views/icons";
import { CustomMenu } from "../../../shared/views/CustomMenu";
import useColors from "../../../hooks/useColors";
import { toast } from "../../../shared/views/CustomToaster";
import { FetchingStatus } from "../../stores/LoadingStore";

const MyProjectsListBase: React.FC = () => {
  const { projectStore, accountStore, loadingStore } = useStores();
  const projectCreationModal = useDisclosure();
  const [red] = useColors(["red.400"]);

  const projectsByCity = useMemo(() => {
    const map = new Map<string, Project[]>();

    for (const project of projectStore.projects) {
      const cityProjects = map.get(project.city) ?? [];
      cityProjects.push(project);
      map.set(project.city, cityProjects);
    }
    return map;
  }, [projectStore.projects]);

  const removeProjectFromLibrary = (projectId: string) => {
    projectStore
      .removeFromLibrary(projectId)
      .then(() => {
        toast("success", t("home:projects.removeProjectToast.success"));
      })
      .catch((error) => {
        if (error?.status === 409) {
          toast("error", t("home:projects.removeProjectToast.conflict"));
        } else {
          toast("error", t("home:projects.removeProjectToast.error"));
        }
      });
  };

  return (
    <VStack
      flexShrink={0}
      w={320}
      overflow="auto"
      alignItems="stretch"
      justifyContent="start"
      py={4}
      px={4}
    >
      <Stack>
        <Text flexGrow={1} px={2} fontWeight="semibold" textAlign="center">
          {t("home:projects.title")}
        </Text>

        <HStack justifyContent={"center"}>
          <Button
            variant="ghost"
            size="sm"
            fontSize={"sm"}
            colorPalette="primary"
            onClick={projectCreationModal.onOpen}
            disabled={
              !accountStore.selectedUserAccount?.permissions?.canManageProjects
            }
          >
            <AddIcon /> {t("home:projects.create")}
          </Button>
        </HStack>

        {loadingStore.fetchingState.projectsList === FetchingStatus.Fetching ? (
          <HStack p={4} justifyContent="center">
            <Spinner color="primary.500" />
          </HStack>
        ) : projectStore.projects.length ? (
          [...projectsByCity.entries()].map(([city, projects]) => (
            <Card.Root variant="subtle" key={city}>
              <Text
                pt={2}
                pl={2}
                lineClamp={1}
                fontSize="2xs"
                textTransform="uppercase"
                fontWeight="semibold"
                opacity={0.6}
              >
                {city}
              </Text>
              <Stack gap={0}>
                {projects.map((project) => (
                  <HStack
                    gap={0}
                    key={project.id}
                    _hover={{
                      textDecor: "underline",
                    }}
                  >
                    <Flex asChild flexGrow={1}>
                      <Link to={appRoutes.project(project.id)}>
                        <Text px={2} py={1} fontSize={"sm"}>
                          {project.name}
                        </Text>
                      </Link>
                    </Flex>
                    <CustomMenu
                      items={[
                        {
                          text: t("home:projects.removeFromLibrary"),
                          icon: <CloseIcon color={red} />,
                          onClick: () => removeProjectFromLibrary(project.id),
                          key: "",
                        },
                      ]}
                    >
                      <IconButton size="sm" variant="subtle">
                        <HamburgerIcon />
                      </IconButton>
                    </CustomMenu>
                  </HStack>
                ))}
              </Stack>
            </Card.Root>
          ))
        ) : (
          <Text p={2} fontSize="sm">
            {t("home:projects.empty")}
          </Text>
        )}
      </Stack>

      {projectCreationModal.open && (
        <CreateOrUpdateProjectModal onClose={projectCreationModal.onClose} />
      )}
    </VStack>
  );
};

export const MyProjectsList = observer(MyProjectsListBase);
